import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';
import PageHeader from '../../components/ui/PageHeader';
import { EmpresaService } from '../../services/EmpresaService';
import { ApiSunatService } from '../../services/ApiSunatService';
import '../Usuario.css';

const tipoComprobanteOptions = [
    { label: 'Factura', value: '01' },
    { label: 'Boleta', value: '03' }
];

const tipoDocumentoOptions = [
    { label: "SIN DOCUMENTO", value: '0' },
    { label: 'DNI', value: '1' },
    { label: 'RUC', value: '6' },
    { label: 'Carnet de Extranjeria', value: '4' },
    { label: 'Pasaporte', value: '7' }
];

const monedaOptions = [
    { label: 'PEN - Soles', value: 'PEN' },
    { label: 'USD - Dolares', value: 'USD' }
];

const createProducto = () => ({
    nombre: '',
    precioVenta: 0,
    cantidad: 1
});

const ApiSunat = () => {
    const toast = useRef(null);

    const [isLoading, setIsLoading] = useState(false);
    const [responseData, setResponseData] = useState(null);

    const [comprobante, setComprobante] = useState({
        fechaEmision: new Date().toISOString().slice(0, 10),
        tipoComprobante: '01',
        serie: 'F001',
        correlativo: 1,
        moneda: 'PEN'
    });

    const [empresa, setEmpresa] = useState({
        ruc: '',
        razonSocial: '',
        nombreComercial: '',
        ubigeo: '',
        departamento: '',
        provincia: '',
        distrito: '',
        direccionFiscal: ''
    });

    const [cliente, setCliente] = useState({
        tipoDocumento: '6',
        numeroDocumento: '',
        nombreRazonSocial: '',
        direccion: '',
        telefono: ''
    });

    const [productos, setProductos] = useState([createProducto()]);

    useEffect(() => {
        const cargarEmpresa = async () => {
            try {
                const empresas = await EmpresaService.getEmpresas();
                if (empresas && empresas.length > 0) {
                    const emp = empresas[0];
                    setEmpresa({
                        ruc: emp.ruc || '',
                        razonSocial: emp.nombre || '',
                        nombreComercial: emp.nombreComercial || '',
                        ubigeo: emp.ubigeo || '',
                        departamento: emp.departamento || '',
                        provincia: emp.provincia || '',
                        distrito: emp.distrito || '',
                        direccionFiscal: emp.direccion || ''
                    });
                }
            } catch (error) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Aviso',
                    detail: 'No se pudo autocompletar datos de empresa',
                    life: 3000
                });
            }
        };

        cargarEmpresa();
    }, []);

    useEffect(() => {
        setComprobante((prev) => ({
            ...prev,
            serie: prev.tipoComprobante === '01' ? 'F001' : 'B001'
        }));
    }, [comprobante.tipoComprobante]);

    const totals = useMemo(() => {
        const subTotal = productos.reduce((acc, item) => {
            const precio = Number(item.precioVenta) || 0;
            const cantidad = Number(item.cantidad) || 0;
            return acc + (precio * cantidad);
        }, 0);

        const operacionGravada = Number((subTotal / 1.18).toFixed(2));
        const igv = Number((subTotal - operacionGravada).toFixed(2));

        return {
            operacionGravada,
            igv,
            subTotal: Number(subTotal.toFixed(2)),
            total: Number(subTotal.toFixed(2)),
            cantidadTotal: productos.reduce((acc, p) => acc + (Number(p.cantidad) || 0), 0)
        };
    }, [productos]);

    const handleEmpresaChange = (name, value) => {
        setEmpresa((prev) => ({ ...prev, [name]: value }));
    };

    const handleClienteChange = (name, value) => {
        setCliente((prev) => ({ ...prev, [name]: value }));
    };

    const handleComprobanteChange = (name, value) => {
        setComprobante((prev) => ({ ...prev, [name]: value }));
    };

    const handleProductoChange = (index, name, value) => {
        setProductos((prev) => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [name]: value
            };
            return updated;
        });
    };

    const addProducto = () => {
        setProductos((prev) => [...prev, createProducto()]);
    };

    const removeProducto = (index) => {
        setProductos((prev) => {
            if (prev.length === 1) return prev;
            return prev.filter((_, i) => i !== index);
        });
    };

    const validarFormulario = () => {
        if (!empresa.ruc || !empresa.razonSocial || !empresa.ubigeo || !empresa.departamento || !empresa.provincia || !empresa.distrito || !empresa.direccionFiscal) {
            return 'Complete todos los datos del comprobante de pago (empresa).';
        }

        if (!cliente.tipoDocumento || !cliente.numeroDocumento || !cliente.nombreRazonSocial) {
            return 'Complete todos los datos obligatorios del cliente.';
        }

        if (!comprobante.tipoComprobante || !comprobante.serie || !comprobante.correlativo || !comprobante.moneda) {
            return 'Complete los datos del comprobante (tipo, serie, correlativo, moneda).';
        }

        const hasInvalidProducto = productos.some((p) => !p.nombre || (Number(p.cantidad) || 0) <= 0 || (Number(p.precioVenta) || 0) <= 0);
        if (hasInvalidProducto) {
            return 'Cada producto debe tener nombre, cantidad y precio de venta mayor a 0.';
        }

        if (totals.total <= 0 || totals.cantidadTotal <= 0) {
            return 'El total de la venta debe ser mayor a 0.';
        }

        return null;
    };

    const generarComprobante = async () => {
        const validationError = validarFormulario();
        if (validationError) {
            toast.current?.show({ severity: 'warn', summary: 'Validacion', detail: validationError, life: 3500 });
            return;
        }

        setIsLoading(true);
        setResponseData(null);

        try {
            const descripcion = productos
                .map((p) => `${p.nombre} x${Number(p.cantidad) || 0}`)
                .join(', ')
                .slice(0, 240);

            const precioPromedioConIgv = totals.cantidadTotal > 0
                ? Number((totals.total / totals.cantidadTotal).toFixed(2))
                : 0;

            const payload = {
                empresa: {
                    ruc: empresa.ruc,
                    razon_social: empresa.razonSocial,
                    nombre_comercial: empresa.nombreComercial || empresa.razonSocial,
                    ubigeo: empresa.ubigeo,
                    departamento: empresa.departamento,
                    provincia: empresa.provincia,
                    distrito: empresa.distrito,
                    direccion: empresa.direccionFiscal
                },
                cliente: {
                    tipo_documento: cliente.tipoDocumento,
                    nro_documento: cliente.numeroDocumento,
                    razon_social: cliente.nombreRazonSocial,
                    direccion: cliente.direccion,
                    telefono: cliente.telefono
                },
                comprobante: {
                    fecha_emision: comprobante.fechaEmision,
                    tipo_comprobante: comprobante.tipoComprobante,
                    serie: comprobante.serie,
                    correlativo: Number(comprobante.correlativo),
                    moneda: comprobante.moneda
                },
                venta: {
                    cantidad: totals.cantidadTotal,
                    precio_total: precioPromedioConIgv,
                    descripcion
                },
                detalle_venta: {
                    op_gravado: totals.operacionGravada,
                    igv: totals.igv,
                    subtotal: totals.subTotal,
                    total: totals.total
                },
                items: productos.map((item) => ({
                    nombre: item.nombre,
                    precio_venta: Number(item.precioVenta) || 0,
                    cantidad: Number(item.cantidad) || 0
                }))
            };

            const result = await ApiSunatService.generarComprobante(payload);
            setResponseData(result);

            toast.current?.show({
                severity: 'success',
                summary: 'Comprobante generado',
                detail: result?.mensaje || 'Comprobante enviado a SUNAT correctamente',
                life: 4000
            });

            if (result?.pdf_base64) {
                const pdfWindow = window.open('', '_blank');
                if (pdfWindow) {
                    pdfWindow.document.write(`<iframe width="100%" height="100%" style="border:none" src="data:application/pdf;base64,${result.pdf_base64}"></iframe>`);
                }
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error SUNAT',
                detail: error.message || 'No se pudo generar el comprobante',
                life: 4500
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="grid usuario-page">
            <Toast ref={toast} />
            <div className="col-12">
                <PageHeader
                    title="API SUNAT"
                    description="Emision de factura o boleta con envio al endpoint SUNAT"
                    icon="pi pi-file"
                />

                <Card>
                    <div className="grid">
                        <div className="col-12">
                            <h5 className="mb-3">1. Comprobante de Pago</h5>
                        </div>

                        <div className="col-12 md:col-4 field">
                            <label>RUC</label>
                            <InputText value={empresa.ruc} onChange={(e) => handleEmpresaChange('ruc', e.target.value.replace(/[^0-9]/g, '').slice(0, 11))} />
                        </div>
                        <div className="col-12 md:col-4 field">
                            <label>Razon Social</label>
                            <InputText value={empresa.razonSocial} onChange={(e) => handleEmpresaChange('razonSocial', e.target.value)} />
                        </div>
                        <div className="col-12 md:col-4 field">
                            <label>Nombre Comercial</label>
                            <InputText value={empresa.nombreComercial} onChange={(e) => handleEmpresaChange('nombreComercial', e.target.value)} />
                        </div>

                        <div className="col-12 md:col-3 field">
                            <label>Ubigeo</label>
                            <InputText value={empresa.ubigeo} onChange={(e) => handleEmpresaChange('ubigeo', e.target.value)} />
                        </div>
                        <div className="col-12 md:col-3 field">
                            <label>Departamento</label>
                            <InputText value={empresa.departamento} onChange={(e) => handleEmpresaChange('departamento', e.target.value)} />
                        </div>
                        <div className="col-12 md:col-3 field">
                            <label>Provincia</label>
                            <InputText value={empresa.provincia} onChange={(e) => handleEmpresaChange('provincia', e.target.value)} />
                        </div>
                        <div className="col-12 md:col-3 field">
                            <label>Distrito</label>
                            <InputText value={empresa.distrito} onChange={(e) => handleEmpresaChange('distrito', e.target.value)} />
                        </div>

                        <div className="col-12 md:col-6 field">
                            <label>Direccion Fiscal</label>
                            <InputText value={empresa.direccionFiscal} onChange={(e) => handleEmpresaChange('direccionFiscal', e.target.value)} />
                        </div>
                        <div className="col-12 md:col-2 field">
                            <label>Fecha de Emision</label>
                            <InputText type="date" value={comprobante.fechaEmision} onChange={(e) => handleComprobanteChange('fechaEmision', e.target.value)} />
                        </div>
                        <div className="col-12 md:col-2 field">
                            <label>Tipo Comprobante</label>
                            <Dropdown value={comprobante.tipoComprobante} options={tipoComprobanteOptions} onChange={(e) => handleComprobanteChange('tipoComprobante', e.value)} />
                        </div>
                        <div className="col-12 md:col-1 field">
                            <label>Serie</label>
                            <InputText value={comprobante.serie} onChange={(e) => handleComprobanteChange('serie', e.target.value.toUpperCase())} />
                        </div>
                        <div className="col-12 md:col-1 field">
                            <label>Correlativo</label>
                            <InputNumber value={comprobante.correlativo} onValueChange={(e) => handleComprobanteChange('correlativo', e.value)} useGrouping={false} min={1} />
                        </div>

                        <div className="col-12 md:col-3 field">
                            <label>Moneda</label>
                            <Dropdown value={comprobante.moneda} options={monedaOptions} onChange={(e) => handleComprobanteChange('moneda', e.value)} />
                        </div>
                    </div>

                    <Divider />

                    <div className="grid">
                        <div className="col-12">
                            <h5 className="mb-3">2. Datos del Cliente</h5>
                        </div>

                        <div className="col-12 md:col-3 field">
                            <label>Tipo de Documento</label>
                            <Dropdown value={cliente.tipoDocumento} options={tipoDocumentoOptions} onChange={(e) => handleClienteChange('tipoDocumento', e.value)} />
                        </div>
                        <div className="col-12 md:col-3 field">
                            <label>Numero de Documento</label>
                            <InputText value={cliente.numeroDocumento} onChange={(e) => handleClienteChange('numeroDocumento', e.target.value)} />
                        </div>
                        <div className="col-12 md:col-6 field">
                            <label>Nombre / Razon Social</label>
                            <InputText value={cliente.nombreRazonSocial} onChange={(e) => handleClienteChange('nombreRazonSocial', e.target.value)} />
                        </div>
                        <div className="col-12 md:col-8 field">
                            <label>Direccion</label>
                            <InputText value={cliente.direccion} onChange={(e) => handleClienteChange('direccion', e.target.value)} />
                        </div>
                        <div className="col-12 md:col-4 field">
                            <label>Telefono</label>
                            <InputText value={cliente.telefono} onChange={(e) => handleClienteChange('telefono', e.target.value)} />
                        </div>
                    </div>

                    <Divider />

                    <div className="grid">
                        <div className="col-12 flex justify-content-between align-items-center">
                            <h5 className="mb-3">3. Listado de Productos</h5>
                            <Button type="button" label="Agregar Producto" icon="pi pi-plus" className="p-button-sm" onClick={addProducto} />
                        </div>

                        {productos.map((producto, idx) => (
                            <React.Fragment key={idx}>
                                <div className="col-12 md:col-5 field">
                                    <label>Nombre del Producto</label>
                                    <InputText value={producto.nombre} onChange={(e) => handleProductoChange(idx, 'nombre', e.target.value)} />
                                </div>
                                <div className="col-12 md:col-3 field">
                                    <label>Precio de Venta (incluye IGV)</label>
                                    <InputNumber value={producto.precioVenta} onValueChange={(e) => handleProductoChange(idx, 'precioVenta', e.value)} mode="decimal" minFractionDigits={2} maxFractionDigits={2} min={0} />
                                </div>
                                <div className="col-12 md:col-2 field">
                                    <label>Cantidad</label>
                                    <InputNumber value={producto.cantidad} onValueChange={(e) => handleProductoChange(idx, 'cantidad', e.value)} useGrouping={false} min={1} />
                                </div>
                                <div className="col-12 md:col-2 field flex align-items-end">
                                    <Button
                                        type="button"
                                        icon="pi pi-trash"
                                        className="p-button-danger p-button-outlined w-full"
                                        disabled={productos.length === 1}
                                        onClick={() => removeProducto(idx)}
                                    />
                                </div>
                            </React.Fragment>
                        ))}
                    </div>

                    <Divider />

                    <div className="grid">
                        <div className="col-12">
                            <h5 className="mb-3">4. Detalle de Venta</h5>
                        </div>
                        <div className="col-12 md:col-3 field">
                            <label>Op. Gravado</label>
                            <InputNumber value={totals.operacionGravada} mode="decimal" minFractionDigits={2} maxFractionDigits={2} disabled />
                        </div>
                        <div className="col-12 md:col-3 field">
                            <label>IGV</label>
                            <InputNumber value={totals.igv} mode="decimal" minFractionDigits={2} maxFractionDigits={2} disabled />
                        </div>
                        <div className="col-12 md:col-3 field">
                            <label>Subtotal</label>
                            <InputNumber value={totals.subTotal} mode="decimal" minFractionDigits={2} maxFractionDigits={2} disabled />
                        </div>
                        <div className="col-12 md:col-3 field">
                            <label>Total</label>
                            <InputNumber value={totals.total} mode="decimal" minFractionDigits={2} maxFractionDigits={2} disabled />
                        </div>
                    </div>

                    {responseData && (
                        <div className="p-3 mt-3 border-round bg-green-50 border-1 border-green-300">
                            <strong>Resultado:</strong> {responseData.mensaje || 'Comprobante generado'}
                            <br />
                            <strong>Archivo:</strong> {responseData.archivo_nombre || '-'}
                            <br />
                            <strong>Hash:</strong> {responseData.hash || '-'}
                        </div>
                    )}

                    <div className="flex justify-content-end mt-4">
                        <Button
                            label={isLoading ? 'Enviando...' : 'Generar Comprobante'}
                            icon={isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
                            onClick={generarComprobante}
                            disabled={isLoading}
                        />
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default ApiSunat;
